import { LinkOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Descriptions, Empty, Form, Input, notification, Tooltip, Typography } from 'antd'
import type { JSX } from 'react'

import { CommunityRegistration, linkTCRCampaign } from 'api/tcr'
import { QUERIES } from 'constants/queries'

type LinkCampaignProps = {
  campaign: CommunityRegistration['campaign'] | undefined
  canWrite?: boolean
  clientId: string
  disabledTooltip?: string
}

export function LinkCampaign({ campaign, canWrite = true, clientId, disabledTooltip }: LinkCampaignProps): JSX.Element {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { mutate: linkCampaign, isLoading } = useMutation({
    mutationFn: linkTCRCampaign,
    onSuccess: () => {
      notification.success({
        message: 'Campaign linked successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      form.resetFields()
      queryClient.invalidateQueries([QUERIES.TCR.COMMUNITY_REGISTRATIONS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to link campaign',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleSubmit = (values: { campaignId: string }) => {
    if (!canWrite) return
    linkCampaign({ clientId, externalCampaignId: values.campaignId.trim() })
  }

  return (
    <Card title="Campaign">
      <Form form={form} layout="inline" onFinish={handleSubmit} style={{ marginBottom: '12px' }}>
        <Form.Item name="campaignId" rules={[{ required: true, message: 'Please enter campaign ID' }]}>
          {canWrite ? (
            <Input placeholder="Campaign ID (e.g., CAMPAIGN456)" style={{ width: 320 }} />
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Input disabled placeholder="Campaign ID (e.g., CAMPAIGN456)" style={{ width: 320 }} />
              </span>
            </Tooltip>
          )}
        </Form.Item>
        <Form.Item>
          {canWrite ? (
            <Button htmlType="submit" icon={<LinkOutlined />} loading={isLoading} type="primary">
              Link Campaign
            </Button>
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Button disabled htmlType="submit" icon={<LinkOutlined />} loading={isLoading} type="primary">
                  Link Campaign
                </Button>
              </span>
            </Tooltip>
          )}
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        Enter campaign ID from the TCR dashboard to link it to this client.
      </Typography.Text>
      {campaign ? (
        <Descriptions bordered column={1} labelStyle={{ width: 200 }} size="small" style={{ marginTop: '16px' }}>
          <Descriptions.Item label="ID">
            <Typography.Text code copyable>
              {campaign.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="External ID">
            <Typography.Text code copyable>
              {campaign.externalId}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Campaign Name">{campaign.campaignName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Description">{campaign.description}</Descriptions.Item>
          <Descriptions.Item label="Use Case">{campaign.useCase ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Sub Use Cases">
            {campaign.subUseCases?.length ? campaign.subUseCases.join(', ') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Inserted At">{campaign.insertedAt}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty description="No campaign linked" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: '16px' }} />
      )}
    </Card>
  )
}
